import { Alert, Box } from "@mui/material";
import { GridRowOrderChangeParams, GridSortDirection, GridSortModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { CustomDataGrid, DeletePanel, MyPaper, TitleHeader } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeleteTagMutation, useLazyGetTagsQuery, useUpdateTagMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateConfirmationAction, updateCongirmationDialogBody } from "../../../redux/slices/confirmationDialog";
import { openDialog } from "../../../redux/slices/dialog";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { updateRowPosition } from "../../viewBOMTree";
import { TagsTableColumn } from "../utils";
import { AddEditTags } from "./addEditTags";

export const TagListing: React.FC<{ children?: JSX.Element }> = (props) => {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    let [getTags, { data: allData = { data: [], totalCount: 0 }, isFetching: isLoading, isError }] = useLazyGetTagsQuery();
    const { data, totalCount } = allData;

    const [updateTag] = useUpdateTagMutation();
		const [deleteTag] = useDeleteTagMutation();

    const [params, setSearchParam] = useSearchParams();

    const [search, setSearch] = React.useState("");

    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 100,
    });
    const [queryOptions, setQueryOptions] = React.useState({
        sortModel: [{ field: 'tag_position', sort: 'asc' as GridSortDirection }],
    });

    const { perm } = useAppSelector(store => store.auth.userDetails);

    const master = React.useMemo(() => HasAccess(perm, PERMISSIONS.TAG_MASTER), [perm]);

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        // Here you save the data you need from the sort model
        setQueryOptions({ sortModel: [...sortModel] });
    }, [])

    const refetch = () => {
        getTags({ search, groupId: params.get("group") === "all" ? '' : params.get("group"), sort_by: queryOptions?.sortModel?.[0]?.field, sort_order: queryOptions?.sortModel?.[0]?.sort, ...paginationModel });
    }

    const openAddEditTagDialog = (isEdit: boolean, data: any) => {
        dispatch(openDialog({
            title: `${isEdit ? "Edit" : "Add"} Tag`,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditTags isEdit={isEdit} data={data} refetch={refetch} totalCount={totalCount} />
        }));
    }

    const onStatusChange = async (data: any, isActive: boolean) => {
        dispatch(openBackdrop("Updating Tag Status..."));
        await updateTag({ id: data?._id, payload: { ...data, status: isActive ? "active" : "inactive" } })
            .then(() => {
                refetch();
            })
            .catch((error) => {
                let errorMessage: string = error?.data?.title ?? "Oops! Something went wrong, Unable to update status";
                enqueueSnackbar(errorMessage, { variant: "warning" });
            })
            .finally(() => {
                dispatch(closeBackdrop());
            })
    }

		const deleteRow = (data: any) => {
			let message = `Do you want to delete this "${data.tag_name}"?`;
			dispatch(openConfirmationDialog({
					title: "Delete Operation",
					body: <DeletePanel
							message={message}
							warningMessage={""}
					/>,
					positiveBtn: "Delete",
					negativeBtn: "Cancel",
					onOk: () => performRowDel(data),
					onNegativeBtn: () => dispatch(closeConfirmationDialog())
			}))
		};

		const performRowDel = async (data: any) => {
			dispatch(openBackdrop("Checking dependencies and deleting tag...."));
			let res: any = {};
			try {

					res = await deleteTag({ id: data._id });

					if (Object.keys(res).includes("data")) {
							enqueueSnackbar(`Deleted ${data.tag_name} Successfully!`, { variant: "success" })
							dispatch(closeConfirmationDialog());
							refetch();
					} else if (Object.keys(res).includes("error")) {
							// eslint-disable-next-line 
							throw "Unable to delete Tag"
					} else {
							// eslint-disable-next-line 
							throw "Data not found"
					}
			} catch (error) {
					let errorMessage: string = res?.error?.data?.title ?? `Unable to delete ${data.tag_name}!`;
					enqueueSnackbar(errorMessage, { variant: "error" });
					dispatch(updateCongirmationDialogBody(<DeletePanel message={"Dependency table of " + data.tag_name} errorMessage={errorMessage} errorMessageStacks={[{ object_type: "tag", object_details: res?.error?.data?.details?.collections ?? [] }]} />));
					dispatch(updateConfirmationAction({
						hideForceBtn: true,
						hideNegativeBtn: true,
						hidePositiveBtn: true
					}))
			} finally {
					dispatch(closeBackdrop());
			}
		};

    const giveMeRows = () => {
        if (!isLoading && data) {
            return (data ?? []).map(d => ({ ...d, __reorder__: d.tag_name }));
        } else {
            return [];
        }
    };

    const ROWS = giveMeRows();

    const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
        dispatch(
            openConfirmationDialog({
                title: "Are you sure?",
                body: `Do you want to move this ${params?.row?.tag_name}?`,
                positiveBtn: "Move",
                negativeBtn: "Cancel",
                onOk: async () => {
                    const newRows = updateRowPosition(params.oldIndex, params.targetIndex, ROWS);
                    await Promise.all(newRows.map((row: any, index: number) => {
                        return updateTag({ id: row._id, payload: { ...row, tag_position: index + 1 } })
                    })).then(() => {
                        refetch();
                    }).catch((error) => {
                        let errorMessage: string = error?.data?.title ?? "Oops! Something went wrong, Unable to update position";
                        enqueueSnackbar(errorMessage, { variant: "warning" });
                    }).finally(() => {
                        dispatch(closeBackdrop());
                        dispatch(closeConfirmationDialog());
                    })
                },
                onNegativeBtn: () => {
                    refetch();
                    dispatch(closeConfirmationDialog());
                },
            })
        );
    };


    React.useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, [params, paginationModel, JSON.stringify(queryOptions?.sortModel)]);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            refetch();
        }, 500)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line
    }, [search]);

    React.useEffect(() => {
        if (params.get('group')) {
            refetch()
        } else {
            setSearchParam({ group: "all" })
        }
        // eslint-disable-next-line
    }, [])
    
    React.useEffect(() => {
        setSearch("");
        // eslint-disable-next-line
    }, [params.get('group')])

    return (
        <MyPaper>
            <>
                {/* Header */}
                <TitleHeader
                    title="Tags"
                    count={ROWS.length}
                    showSearch={true}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder={"Search for Tags"}
                    showCreateBtn={master}
                    createBtnLabel={"Create"}
                    onCreateBtn={() => openAddEditTagDialog(false, null)}
                />

                {/* Error */}
                {isError && <Alert sx={{ mt: 2 }} severity="error">Oops! Something went wrong, Unable to fetch Tags. Try Again Later!</Alert>}

                {/* Data Grid */}
                <Box
                    sx={{
                        mt: 2,
                        height: "calc(100vh - 210px)"
                    }}>
                    <CustomDataGrid
                        id="tags-list"
                        rows={ROWS}
                        columns={TagsTableColumn((data: any) => openAddEditTagDialog(true, data), master, onStatusChange, deleteRow)}
                        rowReordering={params.get("group") !== "all"}
                        onRowOrderChange={handleRowOrderChange}
                        loading={isLoading}
                        getRowId={(row) => row._id}
                        showToolbar={true}
                        rowCount={totalCount}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        onPaginationModelChange={setPaginationModel}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                    />
                </Box>
            </>
        </MyPaper>
    );
}